import { useState, useRef, useEffect, useCallback } from 'react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import {  doubleLongTap } from '../transcriptUtils';
import { isCapacitor } from '../../utils/platform';
import { reportService } from '../../services';
import { createLogger } from '../../services/Debug';
export function useAudioQueue({
  isOnline,
  showNotification,
  setTranscript,
  textareaRef,
  onTranscriptionLimitExceeded,
  onTranscriptionError,
  selectedReportType,
  getProcessedText,
  currentDraftId
}) {
  const log = createLogger('useAudioQueue');
  // Le "queue" local pour stocker les chunks audio avant transcription
  const [queue, setQueue] = useState([]);
  const queueRef = useRef(queue);
  queueRef.current = queue;
  const processingRef = useRef(false);
  const queueMetadataRef = useRef([]);

  const [isTranscribing, setIsTranscribing] = useState(false);

  // Remove retry count state since we only try once

  // États pour la gestion de la queue
  const [queuePaused, setQueuePaused] = useState(false);
  const [manualAirplaneMode, setManualAirplaneMode] = useState(false);
  const [pausedItems, setPausedItems] = useState(new Set());
  const [queueWithMetadata, setQueueWithMetadata] = useState([]);
  // Historique/local library des audios (terminés, en erreur, etc.)
  const [audioLibrary, setAudioLibrary] = useState([]);
  const audioLibraryRef = useRef(audioLibrary);
  audioLibraryRef.current = audioLibrary;

  // Store audio blobs in memory keyed by audio ID
  const audioBlobsRef = useRef(new Map());

  // Track current draft ID for audio management
  const currentDraftIdRef = useRef(currentDraftId);
  currentDraftIdRef.current = currentDraftId;
  
  // Track if queue was paused due to network loss
  const [pausedDueToNetwork, setPausedDueToNetwork] = useState(false);
  const [wasProcessingBeforeOffline, setWasProcessingBeforeOffline] = useState(false);
  
  // Error dialog state
  const [errorDialogState, setErrorDialogState] = useState(null);

  // Fonctions helper pour gérer les métadonnées de queue
  const updateQueueMetadata = useCallback((audioId, updates) => {
    setQueueWithMetadata(prev => 
      prev.map(item => 
        item.id === audioId ? { ...item, ...updates } : item
      )
    );
    // Miroir: mettre à jour aussi la librairie locale si besoin
    setAudioLibrary(prev => prev.map(item => item.id === audioId ? { ...item, ...updates } : item));
  }, []);

  const removeFromQueueMetadata = useCallback((audioId) => {
    const newMetadata = queueMetadataRef.current.filter(item => item.id !== audioId);
    setQueueWithMetadata(newMetadata);
    queueMetadataRef.current = newMetadata;
  }, []);

  const addToQueueMetadata = useCallback((audio, audioId, extra = {}) => {
    const newItem = {
      id: audioId,
      audio: audio,
      status: 'pending',
      timestamp: Date.now(),
      ...extra
    };
    setQueueWithMetadata(prev => [...prev, newItem]);
    queueMetadataRef.current = [...queueMetadataRef.current, newItem];
    // Ajouter à la librairie locale si pas déjà présent
    setAudioLibrary(prev => {
      if (prev.some(i => i.id === audioId)) return prev;
      return [...prev, {
        id: audioId,
        audio,
        status: 'pending',
        createdAt: Date.now(),
        errorMessage: null,
        durationSeconds: typeof extra.durationSeconds === 'number' ? extra.durationSeconds : undefined
      }];
    });
  }, []);

  // Save audio library to localStorage for a specific draft
  const saveAudioLibraryForDraft = useCallback((draftId) => {
    if (!draftId) return;
    try {
      const libraryMetadata = audioLibraryRef.current.map(item => ({
        id: item.id,
        status: item.status,
        createdAt: item.createdAt,
        errorMessage: item.errorMessage,
        durationSeconds: item.durationSeconds,
        // Store blob reference but not the blob itself
        hasAudio: !!item.audio
      }));
      localStorage.setItem(`audioLibrary_${draftId}`, JSON.stringify(libraryMetadata));
      log.info(`Saved audio library for draft ${draftId}:`, libraryMetadata.length, 'items');
    } catch (e) {
      log.error('Failed to save audio library:', e);
    }
  }, []);

  // Load audio library from localStorage for a specific draft
  const loadAudioLibraryForDraft = useCallback((draftId) => {
    if (!draftId) {
      // Clear if no draft
      setAudioLibrary([]);
      audioBlobsRef.current.clear();
      return;
    }
    try {
      const stored = localStorage.getItem(`audioLibrary_${draftId}`);
      if (stored) {
        const libraryMetadata = JSON.parse(stored);
        // Restore metadata but blobs will need to be re-recorded
        const restoredLibrary = libraryMetadata
          .map(item => ({
            ...item,
            audio: audioBlobsRef.current.get(item.id) || null
          }))
          .filter(item => item.audio !== null);  // Filtrer les items sans blob disponible

        // Log si des items ont été perdus
        const lostCount = libraryMetadata.length - restoredLibrary.length;
        if (lostCount > 0) {
          log.warn(`${lostCount} audio file(s) lost for draft ${draftId} (blobs not in memory)`);
        }

        setAudioLibrary(restoredLibrary);
        log.info(`Loaded audio library for draft ${draftId}:`, restoredLibrary.length, 'items');
      } else {
        setAudioLibrary([]);
      }
    } catch (e) {
      log.error('Failed to load audio library:', e);
      setAudioLibrary([]);
    }
  }, []);

  // Clear audio library for a specific draft
  const clearAudioLibraryForDraft = useCallback((draftId) => {
    if (!draftId) return;
    try {
      localStorage.removeItem(`audioLibrary_${draftId}`);
      // Also remove blobs from memory for this draft
      const library = audioLibraryRef.current;
      library.forEach(item => {
        audioBlobsRef.current.delete(item.id);
      });
      log.info(`Cleared audio library for draft ${draftId}`);
    } catch (e) {
      log.error('Failed to clear audio library:', e);
    }
  }, []);

  const addToQueue = useCallback((audio, audioId, metadata = {}) => {

    // Store the blob in memory
    audioBlobsRef.current.set(audioId, audio);

    // Always add to queue metadata
    addToQueueMetadata(audio, audioId, metadata);

    // Add to queue
    setQueue((prev) => {
      const newQueue = [...prev, audio];
      return newQueue;
    });

    // Save to localStorage for current draft
    if (currentDraftIdRef.current) {
      saveAudioLibraryForDraft(currentDraftIdRef.current);
    }

    // If offline, show notification but still queue the audio
    if (!isOnline) {
      showNotification('Audio enregistré hors ligne. Sera transcrit une fois en ligne.', 'info');
    }
  }, [addToQueueMetadata, isOnline, showNotification, saveAudioLibraryForDraft]);

  // Handle transcription error by showing dialog
  const handleTranscriptionError = useCallback((audioId, error, audioIndex) => {
    // Determine user-friendly error message
    let errorMessage = 'Une erreur est survenue lors de la transcription.';
    
    if (!isOnline) {
      errorMessage = 'Pas de connexion internet. Impossible de transcrire l\'audio.';
    } else if (error.code === 'AUDIO_TOO_SHORT') {
      errorMessage = 'L\'audio est trop court ou de mauvaise qualité pour être transcrit.';
    } else if (error.code === 'LIMIT_EXCEEDED') {
      errorMessage = 'Limite quotidienne de transcription atteinte.';
    } else if (error.message?.includes('timeout')) {
      errorMessage = 'La requête a expiré. Veuillez vérifier votre connexion.';
    } else if (error.response?.status === 503) {
      errorMessage = 'Le service de transcription est temporairement indisponible.';
    } else if (error.response?.status === 401) {
      errorMessage = 'Erreur d\'authentification. Veuillez vous reconnecter.';
    }
    
    // Set error dialog state
    setErrorDialogState({
      audioId,
      errorMessage,
      itemDescription: `Segment audio #${audioIndex + 1}`,
      error
    });
    
    // Update metadata to show error status
    updateQueueMetadata(audioId, { status: 'error' });
  }, [isOnline, updateQueueMetadata]);

// Then update the transcribePartialAudio function:
async function transcribePartialAudio(audioBlob, audioId) {
  let timeoutId;
  const slowResponseWarningTime = 20000;

  try {
    timeoutId = setTimeout(() => {
      showNotification('⚠️ La connexion semble lente.', 'warning');
      doubleLongTap();
    }, slowResponseWarningTime);
    // Determine filename and MIME type from blob
    let fileName = isCapacitor ? 'audio.m4a' : 'audio.webm'; // Default for web
    let fileType = audioBlob.type || (isCapacitor ? 'audio/m4a' : 'audio/webm');


    log.info('📤 Transcribing audio:', fileName, 'type:', fileType, 'size:', audioBlob.size, 'bytes');

    // Create File object for reportService
    const fileData = new File([audioBlob], fileName, { 
      type: fileType 
    });


    // Build optional context
    const processedText = typeof getProcessedText === 'function' ? (getProcessedText() || '') : '';
    const options = {};
    if (selectedReportType) {
      options.reportType = selectedReportType;
    }
    if (processedText && processedText.trim().length > 0) {
      options.processedText = processedText;
    }
    // Pull live-measured duration from metadata if available
    try {
      const meta = queueMetadataRef.current.find(item => item.id === audioId);
      if (meta && typeof meta.durationSeconds === 'number' && Number.isFinite(meta.durationSeconds)) {
        options.durationSeconds = meta.durationSeconds;
      }
    } catch (_) {}

    // Use the reportService to transcribe with context
    const transcriptionText = await reportService.transcribeAudio(fileData, {
      ...options,
      forReportGeneration: true
    });

    clearTimeout(timeoutId);

    setTranscript(prev => {
      if (isCapacitor) {
        Haptics.impact({ style: ImpactStyle.Medium });
      }
      const newText = prev + transcriptionText + ' ';

      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        }
      }, 100);

      return newText;
    });
  } catch (err) {
    clearTimeout(timeoutId);
    log.error(`Erreur transcription audio ${audioId}:`, err);
    
    // Handle audio too small error
    if (err.message?.includes('too small')) {
      log.warn('⚠️ Audio too small for transcription');
      throw { code: 'AUDIO_TOO_SHORT' };
    }
    
    // Check if it's a limit exceeded error
    if (err.code === 'LIMIT_EXCEEDED') {
      if (onTranscriptionLimitExceeded) {
        onTranscriptionLimitExceeded(err.details);
      }
    }
    
    throw err;
  }
}

  // Process la file d'attente pour faire la transcription
  const processQueue = useCallback(async (forcedQueue) => {
    if (processingRef.current) {
      return;
    }
    
    // Add window check at the start
    if (window.electronAPI && typeof window.electronAPI.isWindowClosing === 'function') {
      const isClosing = await window.electronAPI.isWindowClosing();
      if (isClosing) {
        return;
      }
    }
    
    processingRef.current = true;
    setIsTranscribing(true);
  
    try {
      let localQueue = forcedQueue || [...queueRef.current];
      let localMetadata = queueMetadataRef.current;
      
      while (localQueue.length > 0) {
        // Check if window is still valid before each iteration
        if (window.electronAPI && typeof window.electronAPI.isWindowClosing === 'function') {
          const isClosing = await window.electronAPI.isWindowClosing();
          if (isClosing) {
            break;
          }
        }

        // Vérifier la synchronisation des arrays
        if (localQueue.length !== localMetadata.length) {
          
          // Try to recover by removing excess items from the longer array
          const minLength = Math.min(localQueue.length, localMetadata.length);
          if (minLength > 0) {
            localQueue = localQueue.slice(0, minLength);
            localMetadata = localMetadata.slice(0, minLength);
            
            // Update the refs and state
            setQueue(localQueue);
            queueRef.current = localQueue;
            setQueueWithMetadata(localMetadata);
            queueMetadataRef.current = localMetadata;
            
          } else {
            // Force complete reset
            setQueue([]);
            queueRef.current = [];
            setQueueWithMetadata([]);
            queueMetadataRef.current = [];
            break;
          }
        }
        
        // Vérifier si la queue globale est en pause
        if (queuePaused) {
          break;
        }
        
        // Vérifier la connexion internet
        if (!isOnline) {
          if (!pausedDueToNetwork) {
            showNotification('Pas de connexion internet, transcription mise en pause...', 'warning');
            setPausedDueToNetwork(true);
            setWasProcessingBeforeOffline(true);
          }
          break;
        }

        const first = localQueue[0];
        const firstMetadata = localMetadata[0];
        
        // Vérifier que l'audio existe
        if (!first || !firstMetadata) {
          log.error('Invalid queue item detected, removing from queue');
          localQueue = localQueue.slice(1);
          localMetadata = localMetadata.slice(1);
          setQueue(localQueue);
          queueRef.current = localQueue;
          setQueueWithMetadata(localMetadata);
          queueMetadataRef.current = localMetadata;
          continue;
        }
        
        const audioId = firstMetadata.id;
        
        // Vérifier si cet élément est en pause ou en échec
        if (pausedItems.has(audioId) || firstMetadata.status === 'failed') {
          // Passer au suivant
          localQueue = localQueue.slice(1);
          localMetadata = localMetadata.slice(1);
          continue;
        }
        
        // Mettre à jour les métadonnées pour montrer qu'on traite cet élément
        updateQueueMetadata(audioId, { status: 'processing' });
        
        try {
          // Get current transcript for context
          const currentTranscript = document.querySelector('.dictaphone-textarea')?.value || '';
          
          await transcribePartialAudio(first, audioId, currentTranscript);
          
          // Succès : on retire l'audio de la queue
          localQueue = localQueue.slice(1);
          localMetadata = localMetadata.slice(1);
          removeFromQueueMetadata(audioId);
          // Supprimer complètement le fichier audio de la bibliothèque (transcription réussie)
          setAudioLibrary(prev => prev.filter(item => item.id !== audioId));
          // Supprimer le blob de la mémoire
          audioBlobsRef.current.delete(audioId);
          // Sauvegarder dans localStorage
          setTimeout(() => {
            if (currentDraftIdRef.current) {
              saveAudioLibraryForDraft(currentDraftIdRef.current);
            }
          }, 100);
        } catch (error) {
          
          // Check if it's a limit exceeded error
          if (error.code === 'LIMIT_EXCEEDED') {
            
            // Clear the entire queue
            localQueue = [];
            localMetadata = [];
            setQueue([]);
            queueRef.current = [];
            setQueueWithMetadata([]);
            queueMetadataRef.current = [];
            
            // Don't retry, exit the loop
            break;
          }
          
          // Find the index of the current audio in the original queue
          const audioIndex = queueMetadataRef.current.findIndex(item => item.id === audioId);
          
          // Show error dialog and pause processing - only try once
          handleTranscriptionError(audioId, error, audioIndex);
          // Marquer en erreur dans la librairie locale
          setAudioLibrary(prev => prev.map(item => item.id === audioId ? { ...item, status: 'error', errorMessage: error?.message || 'Erreur de transcription' } : item));
          
          // Call the parent error handler if provided
          if (onTranscriptionError) {
            onTranscriptionError({
              audioId,
              error,
              audioIndex,
              onRetry: () => handleErrorRetry(audioId),
              onDelete: () => handleErrorDelete(audioId)
            });
          }
          
          // Remove the failed item from queue to prevent spam
          localQueue = localQueue.slice(1);
          localMetadata = localMetadata.slice(1);
          
          // Update the queue state
          setQueue(localQueue);
          queueRef.current = localQueue;
          queueMetadataRef.current = localMetadata;
          
          // Mark as failed in metadata but keep it for potential retry
          updateQueueMetadata(audioId, { status: 'failed' });
          
          // Pause processing until user makes a decision
          break;
        }
        
        // Mise à jour de la queue dans l'état
        setQueue(localQueue);
        queueRef.current = localQueue;
        queueMetadataRef.current = localMetadata;
      }
    } finally {
      processingRef.current = false;
      // Ne pas cacher l'indicateur si on a des éléments en pause
      if (queueRef.current.length === 0 || !Array.from(pausedItems).length) {
        setIsTranscribing(false);
      }
    }
  }, [
    queuePaused, isOnline, showNotification, pausedItems, 
    updateQueueMetadata, transcribePartialAudio, 
    removeFromQueueMetadata, onTranscriptionLimitExceeded,
    handleTranscriptionError, onTranscriptionError
  ]);

  // Resume transcription when network comes back
  useEffect(() => {
    if (isOnline && pausedDueToNetwork) {
      setPausedDueToNetwork(false);
      
      if (wasProcessingBeforeOffline && queueRef.current.length > 0 && !processingRef.current && !queuePaused) {

        showNotification('Connexion rétablie, reprise de la transcription...', 'success');
        setWasProcessingBeforeOffline(false);
        processQueue();
      }
    }
  }, [isOnline, pausedDueToNetwork, wasProcessingBeforeOffline, queuePaused, processQueue, showNotification]);

  // Auto-start transcription when new items are added to the queue
  useEffect(() => {

    if (isOnline && queue.length > 0 && !processingRef.current && !queuePaused) {
      processQueue();
    }
  }, [queue.length, isOnline, queuePaused, processQueue]);

  // Queue management functions
  const handlePauseItem = useCallback((audioId) => {
    setPausedItems(prev => new Set(prev).add(audioId));
    updateQueueMetadata(audioId, { status: 'paused' });
  }, [updateQueueMetadata]);

  const handleResumeItem = useCallback((audioId) => {
    setPausedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(audioId);
      return newSet;
    });
    updateQueueMetadata(audioId, { status: 'pending' });
    // Relancer le traitement si nécessaire
    if (!processingRef.current && queueRef.current.length > 0) {
      processQueue();
    }
  }, [updateQueueMetadata, processQueue]);

  const handleDeleteItem = useCallback((audioId) => {
    // Trouver l'index dans les métadonnées
    const metadataIndex = queueMetadataRef.current.findIndex(item => item.id === audioId);
    if (metadataIndex !== -1) {
      // S'assurer que les deux arrays ont la même longueur avant la suppression
      if (queueRef.current.length === queueMetadataRef.current.length) {
        // Retirer de la queue audio à la même position
        const newQueue = [...queueRef.current];
        newQueue.splice(metadataIndex, 1);
        setQueue(newQueue);
        queueRef.current = newQueue;
        
        // Retirer des métadonnées
        const newMetadata = [...queueMetadataRef.current];
        newMetadata.splice(metadataIndex, 1);
        setQueueWithMetadata(newMetadata);
        queueMetadataRef.current = newMetadata;
        
        // Retirer des pauses si nécessaire
        setPausedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(audioId);
          return newSet;
        });
      } else {
        log.error('Queue synchronization error: lengths do not match');
        // Force resync by clearing everything
        clearQueue();
      }
    }
  }, []);

  const handlePauseAll = useCallback(() => {
    setQueuePaused(true);
    setManualAirplaneMode(true);
  }, []);

  const handleResumeAll = useCallback(() => {
    setQueuePaused(false);
    setManualAirplaneMode(false);
    // Relancer le traitement
    if (!processingRef.current && queueRef.current.length > 0) {
      processQueue();
    }
  }, [processQueue]);

  const handleDeleteAll = useCallback(() => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer toute la file d\'attente ?')) {
      clearQueue();
    }
  }, []);

  // Error handling functions
  const handleErrorRetry = useCallback((audioId) => {
    // Clear error dialog
    setErrorDialogState(null);

    // Find the failed item in audioLibrary (it's removed from queueMetadata on error)
    const failedItem = audioLibraryRef.current.find(item => item.id === audioId);
    if (failedItem && failedItem.audio) {
      // Add audio back to queue for retry
      setQueue(prev => [...prev, failedItem.audio]);
      queueRef.current = [...queueRef.current, failedItem.audio];

      // Re-add metadata to queueMetadataRef so queue and metadata stay in sync
      const metadataItem = {
        id: audioId,
        audio: failedItem.audio,
        status: 'pending',
        timestamp: Date.now(),
        durationSeconds: failedItem.durationSeconds
      };
      queueMetadataRef.current = [...queueMetadataRef.current, metadataItem];
      setQueueWithMetadata(prev => [...prev, metadataItem]);

      // Update audioLibrary status to pending
      setAudioLibrary(prev => prev.map(item => item.id === audioId ? { ...item, status: 'pending', errorMessage: null } : item));

      // Resume processing if not already running
      if (!processingRef.current && queueRef.current.length > 0) {
        processQueue();
      }
    } else {
      log.error('Failed item not found or has no audio data for audioId:', audioId);
    }
  }, [processQueue]);

  const handleErrorDelete = useCallback((audioId) => {
    // Clear error dialog
    setErrorDialogState(null);
    
    // Remove from metadata completely
    const newMetadata = queueMetadataRef.current.filter(item => item.id !== audioId);
    setQueueWithMetadata(newMetadata);
    queueMetadataRef.current = newMetadata;
    // Remove from library as well
    setAudioLibrary(prev => prev.filter(item => item.id !== audioId));
    
    // Continue processing remaining items if any
    if (!processingRef.current && queueRef.current.length > 0) {
      processQueue();
    }
  }, [processQueue]);

  // Relancer la transcription pour un élément de la librairie
  const retryTranscription = useCallback((audioId) => {
    const item = audioLibraryRef.current.find(i => i.id === audioId);
    if (!item || !item.audio) return;
    // Ajouter dans la queue pour retraitement
    setQueue(prev => [...prev, item.audio]);
    queueRef.current = [...queueRef.current, item.audio];
    // S'assurer que les métadonnées contiennent l'élément
    const metaExists = queueMetadataRef.current.some(m => m.id === audioId);
    if (!metaExists) {
      const extra = {};
      if (typeof item.durationSeconds === 'number') extra.durationSeconds = item.durationSeconds;
      addToQueueMetadata(item.audio, audioId, extra);
    }
    // Mettre à jour les statuts
    updateQueueMetadata(audioId, { status: 'pending' });
    setAudioLibrary(prev => prev.map(a => a.id === audioId ? { ...a, status: 'pending', errorMessage: null } : a));
    // Lancer le traitement
    if (!processingRef.current && !queuePaused) {
      processQueue();
    }
  }, [addToQueueMetadata, updateQueueMetadata, processQueue, queuePaused]);

  // Nettoyer toute la librairie locale (utilisé uniquement lors d'une suppression explicite)
  const clearAudioLibrary = useCallback(() => {
    setAudioLibrary([]);
    audioLibraryRef.current = [];
  }, []);

  // Supprimer un élément spécifique de la librairie (et de la queue si présent)
  const deleteAudioFromLibrary = useCallback((audioId) => {
    // Retirer des métadonnées et de la queue si présent
    const metadataIndex = queueMetadataRef.current.findIndex(item => item.id === audioId);
    if (metadataIndex !== -1) {
      // Si les longueurs correspondent, retirer aussi l'audio de la queue
      if (queueRef.current.length === queueMetadataRef.current.length) {
        const newQueue = [...queueRef.current];
        newQueue.splice(metadataIndex, 1);
        setQueue(newQueue);
        queueRef.current = newQueue;
      }
      const newMetadata = [...queueMetadataRef.current];
      newMetadata.splice(metadataIndex, 1);
      setQueueWithMetadata(newMetadata);
      queueMetadataRef.current = newMetadata;
    }
    // Retirer de la librairie locale
    setAudioLibrary(prev => prev.filter(item => item.id !== audioId));
  }, []);

  const clearQueue = useCallback(() => {
    
    
    // Force stop any ongoing processing immediately
    processingRef.current = false;
    setIsTranscribing(false);
    
    // Clear all audio blobs from memory
    const currentQueue = queueRef.current;
    const currentMetadata = queueMetadataRef.current;

    // Log what we're clearing for debugging


    // NOTE: Ne PAS close les blobs ici car ils peuvent être réutilisés
    // si le brouillon est réouvert plus tard. Les blobs sont stockés dans
    // audioBlobsRef et doivent rester disponibles. Le garbage collector
    // les nettoiera automatiquement quand ils ne seront plus référencés.
    
    // Reset all queue states to empty
    setQueue([]);
    queueRef.current = [];
    setQueueWithMetadata([]);
    queueMetadataRef.current = [];
    setPausedItems(new Set());
    // Ne désactiver le mode avion QUE si le mode global est désactivé
    const isGlobalAirplane = localStorage.getItem('global_airplane_mode') === '1';
    if (!isGlobalAirplane) {
      setQueuePaused(false);
      setManualAirplaneMode(false);
    }
    // Si le mode global est actif, on garde queuePaused et manualAirplaneMode à true
    // Ne pas supprimer la librairie locale ici: on garde l'historique
    
    // Force a small delay to ensure state updates propagate
    setTimeout(() => {
      // Double-check that everything is cleared
      if (queueRef.current.length > 0 || queueMetadataRef.current.length > 0) {
        log.error('⚠️ Queue not fully cleared, forcing second clear');
        queueRef.current = [];
        queueMetadataRef.current = [];
        setQueue([]);
        setQueueWithMetadata([]);
      }
      
    }, 100);
  }, []);

  // Effect to load audio library when draft changes
  useEffect(() => {
    if (currentDraftId) {
      loadAudioLibraryForDraft(currentDraftId);
    } else {
      setAudioLibrary([]);
    }
  }, [currentDraftId, loadAudioLibraryForDraft]);

  // Effect to save audio library when it changes
  useEffect(() => {
    if (currentDraftId && audioLibrary.length > 0) {
      saveAudioLibraryForDraft(currentDraftId);
    }
  }, [audioLibrary, currentDraftId, saveAudioLibraryForDraft]);

  return {
    queue,
    queueRef,
    processingRef,
    isTranscribing,
    queuePaused,
    manualAirplaneMode,
    pausedItems,
    queueWithMetadata,
    audioLibrary,
    audioLibraryRef,
    clearAudioLibrary,
    deleteAudioFromLibrary,
    addToQueue,
    processQueue,
    clearQueue,
    setQueuePaused,
    handlePauseItem,
    handleResumeItem,
    handleDeleteItem,
    handlePauseAll,
    handleResumeAll,
    handleDeleteAll,
    setQueue,
    setQueueWithMetadata,
    queueMetadataRef,
    addToQueueMetadata,
    errorDialogState,
    handleErrorRetry,
    handleErrorDelete,
    setErrorDialogState,
    retryTranscription,
    saveAudioLibraryForDraft,
    loadAudioLibraryForDraft,
    clearAudioLibraryForDraft
  };
}